import React from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BookOpen, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";

/**
 * Audit Log — Partner API integration-guide URLs.
 *
 * Displays the two public endpoints (Test + Production) that serve
 * `/app/backend/docs/INTEGRATION_GUIDE.md` so ops staff can copy the URL
 * straight from the platform when onboarding a new partner. Static — no
 * network calls, no state — just labeled links with copy buttons.
 */
const URLS = [
  {
    key: "test",
    label: "Test",
    tone: "bg-amber-100 text-amber-800 border-amber-200",
    url: "https://pharmacy-dispatch-3.preview.emergentagent.com/api/docs/integration-guide",
  },
  {
    key: "prod",
    label: "Production",
    tone: "bg-emerald-100 text-emerald-800 border-emerald-200",
    url: "https://srcipharmroute.us/api/docs/integration-guide",
  },
];

export default function IntegrationGuideUrlsPanel() {
  const copy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("URL copied");
    } catch {
      toast.error("Could not copy — select and copy manually.");
    }
  };

  return (
    <Card className="p-5 rounded-2xl" data-testid="integration-guide-urls-panel">
      <div className="flex items-start gap-3 mb-4">
        <div className="p-2 rounded-lg bg-primary/10 text-primary shrink-0">
          <BookOpen className="h-4 w-4" />
        </div>
        <div>
          <h2 className="text-lg font-semibold tracking-tight">Partner API — Integration Guide URLs</h2>
          <p className="text-xs text-muted-foreground mt-0.5 max-w-2xl">
            Public (no-auth) links you can share with pharmacies or third-party systems that want to connect to SRCI Pharm-Route.
            The Test URL is always live; the Production URL becomes reachable after the next Redeploy.
          </p>
        </div>
      </div>

      <div className="space-y-2.5">
        {URLS.map((r) => (
          <div
            key={r.key}
            className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-secondary/40 px-3 py-2"
            data-testid={`integration-guide-url-row-${r.key}`}
          >
            <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full border ${r.tone}`}>
              {r.label}
            </span>
            <code
              className="text-[12px] font-mono-num flex-1 min-w-[240px] truncate select-all"
              data-testid={`integration-guide-url-${r.key}`}
              title={r.url}
            >
              {r.url}
            </code>
            <Button
              size="sm"
              variant="ghost"
              className="h-8 px-2 rounded-full"
              onClick={() => copy(r.url)}
              data-testid={`integration-guide-copy-${r.key}`}
              title="Copy URL"
            >
              <Copy className="h-3.5 w-3.5 mr-1" /> Copy
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-8 px-2 rounded-full"
              asChild
              data-testid={`integration-guide-open-${r.key}`}
            >
              <a href={r.url} target="_blank" rel="noopener noreferrer" title="Open in new tab">
                <ExternalLink className="h-3.5 w-3.5 mr-1" /> Open
              </a>
            </Button>
          </div>
        ))}
      </div>

      <p className="mt-3 text-[11px] text-muted-foreground">
        Tip: append <code className="font-mono-num">?download=true</code> to either URL to force a
        file download instead of an in-browser preview.
      </p>
    </Card>
  );
}
